import { ChevronRight } from "lucide-react";
import type { BizminerImprovementLever } from "@/types/serializers";

interface ImprovementLeverCardProps {
  improvementLever: BizminerImprovementLever;
  onClick: () => void;
}

export default function ImprovementLeverCard({
  improvementLever,
  onClick,
}: ImprovementLeverCardProps) {
  const formatCurrency = (
    profit: { value: number; currency: string; description?: string } | null,
  ) => {
    if (!profit || profit.value == null) return "N/A";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: profit.currency || "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(profit.value);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="bg-card border border-border rounded-lg shadow-sm hover:shadow-md transition-[box-shadow,background-color,border-color] duration-200 hover:bg-muted/20 hover:border-foreground/20 group flex flex-col h-full text-left w-full"
    >
      <div className="p-6 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
              {improvementLever.title}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {improvementLever.journey_stage}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
        </div>
      </div>

      <div className="px-6 py-3 bg-muted/50 border-y border-border">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Profit
            </div>
            <div className="text-sm font-semibold text-foreground mt-1">
              {formatCurrency(improvementLever.additional_profit)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Impact
            </div>
            <div className="text-sm font-semibold text-foreground mt-1">
              {improvementLever.impact}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              Effort
            </div>
            <div className="text-sm font-semibold text-foreground mt-1">
              {improvementLever.effort}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 space-y-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            Pain Point
          </p>
          <p className="text-sm text-foreground">
            {improvementLever.pain_point?.title}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            Solution
          </p>
          <p className="text-sm text-foreground">
            {improvementLever.solution?.title}
          </p>
        </div>
      </div>
    </button>
  );
}
